import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  LinearProgress,
  MenuItem,
  Select,
  Switch,
  Typography,
  Alert,
  Link
} from '@mui/material';
import { CloudUpload, GetApp } from '@mui/icons-material';
import axios from 'axios';
import { API_ENDPOINTS } from '../../../Api/endpoints';

const EmployeeCsvImport = ({ open, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [delimiter, setDelimiter] = useState(',');
  const [hasHeader, setHasHeader] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [importResult, setImportResult] = useState(null);

  // Get auth token from localStorage
  const getAuthToken = () => localStorage.getItem('authToken');

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile && selectedFile.type !== 'text/csv') {
      setError('Please select a CSV file');
      setFile(null);
    } else {
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const token = getAuthToken();
      const response = await axios.get(API_ENDPOINTS.EMPLOYEES.IMPORT_TEMPLATE, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob'
      });

      // Create a download link and trigger the download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'employee_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading template:', error);
      setError('Failed to download template. Please try again.');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!file) {
      setError('Please select a CSV file');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError(null);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('csvFile', file); // Changed from 'file' to 'csvFile' to match backend expectations
      formData.append('delimiter', delimiter);
      formData.append('headerIncluded', hasHeader); // Changed from 'hasHeader' to 'headerIncluded' to match backend expectations

      const token = getAuthToken();
      const response = await axios.post(API_ENDPOINTS.EMPLOYEES.IMPORT, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });

      setImportResult(response.data.data);
      
      // If import was successful with no errors, close the modal after a delay
      if (response.data.data.totalSuccess > 0 && response.data.data.totalErrors === 0) {
        setTimeout(() => {
          onSuccess(response.data.data);
        }, 2000);
      }
    } catch (error) {
      console.error('Error importing employees:', error);
      if (error.response) {
        setError(`Import failed: ${error.response.data.message || 'Unknown error'}`);
      } else {
        setError('Import failed. Please check your file and try again.');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setFile(null);
      setError(null);
      setImportResult(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={uploading}
    >
      <DialogTitle>Import Employees from CSV</DialogTitle>
      <DialogContent>
        <DialogContentText paragraph>
          Upload a CSV file to bulk import employees. You can download a template to see the required format.
        </DialogContentText>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {importResult && (
          <Alert 
            severity={importResult.totalErrors > 0 ? "warning" : "success"}
            sx={{ mb: 2 }}
          >
            <Typography variant="body1" gutterBottom>
              Import completed: {importResult.totalSuccess} employees imported successfully, {importResult.totalErrors} errors.
            </Typography>
            {importResult.totalErrors > 0 && importResult.errors && (
              <Box mt={1}>
                <Typography variant="subtitle2">Error details:</Typography>
                <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                  {importResult.errors.map((error, index) => (
                    <li key={index}>
                      <Typography variant="body2">
                        Row {error.rowNumber}: {error.message}
                      </Typography>
                    </li>
                  ))}
                </ul>
              </Box>
            )}
          </Alert>
        )}
        
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box 
                border={1} 
                borderRadius={1} 
                borderColor="divider" 
                p={3} 
                textAlign="center"
                sx={{
                  borderStyle: 'dashed',
                  backgroundColor: 'background.paper',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    cursor: 'pointer'
                  }
                }}
                onClick={() => document.getElementById('csv-file-input').click()}
              >
                <input
                  id="csv-file-input"
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  disabled={uploading}
                />
                <CloudUpload fontSize="large" color="primary" />
                <Typography variant="h6" mt={1}>
                  {file ? file.name : 'Click to select CSV file or drag and drop'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Only CSV files are accepted
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <Typography variant="subtitle2" gutterBottom>
                  CSV Delimiter
                </Typography>
                <Select
                  value={delimiter}
                  onChange={(e) => setDelimiter(e.target.value)}
                  disabled={uploading}
                >
                  <MenuItem value=",">Comma (,)</MenuItem>
                  <MenuItem value=";">Semicolon (;)</MenuItem>
                  <MenuItem value="\t">Tab</MenuItem>
                </Select>
                <FormHelperText>Select the delimiter used in your CSV file</FormHelperText>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={hasHeader}
                    onChange={(e) => setHasHeader(e.target.checked)}
                    disabled={uploading}
                    color="primary"
                  />
                }
                label="File has header row"
              />
              <FormHelperText>
                Check if your CSV file includes a header row with column names
              </FormHelperText>
            </Grid>
            
            <Grid item xs={12}>
              <Box display="flex" justifyContent="center">
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<GetApp />}
                  onClick={handleDownloadTemplate}
                  disabled={uploading}
                >
                  Download CSV Template
                </Button>
              </Box>
            </Grid>
            
            {uploading && (
              <Grid item xs={12}>
                <Box mt={2}>
                  <Typography variant="body2" gutterBottom>
                    Uploading and processing: {uploadProgress}%
                  </Typography>
                  <LinearProgress variant="determinate" value={uploadProgress} />
                </Box>
              </Grid>
            )}
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary" 
          disabled={!file || uploading}
        >
          Import
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmployeeCsvImport;
